import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CdwUserCollectionEditComponent } from '@cdw-ae/cdw-user-collection-edit';
import {
  AccessManagementUserCollectionRecord,
  AccessManagementUserCollectionUserRecord,
  UserRecord,
} from '@cdw-ae/common-data';
import {
  CdwConfirmationDialogComponent,
  CdwConfirmationDialogData,
  CdwDialogModule,
  ConfirmationDialogResult,
} from '@cdw-ae/common-ui-components-dialog';
import { ErrorSnackbarComponent } from '@cdw-ae/components-error-snackbar';
import { firstValueFrom, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { AccessManagementFrontendAuthService } from '../../access-management-frontend-auth.service';
import { UsersDataService } from '../../users/users-data.service';
import { UserCollectionCsvUploadDialogComponent } from '../user-collection-csv-upload-dialog/user-collection-csv-upload-dialog.component';
import { UserCollectionsDataService } from '../user-collections-data.service';

@Component({
  selector: 'g4c-user-collection-edit',
  templateUrl: './user-collection-edit.component.html',
  styleUrls: ['./user-collection-edit.component.scss'],
  imports: [CdwUserCollectionEditComponent, CommonModule, CdwDialogModule],
})
export class UserCollectionEditComponent implements OnInit, OnDestroy {
  @ViewChild('userCollectionEdit')
  public userCollectionEditComponent!: CdwUserCollectionEditComponent;
  public currentUserCollectionUsers!: AccessManagementUserCollectionUserRecord[];
  public currentUserCollection!: AccessManagementUserCollectionRecord;
  public selectedUserCollectionId!: number | 'new';
  public isLoading = false;
  public users: UserRecord[] = [];
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private userCollectionService: UserCollectionsDataService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private snackbar: MatSnackBar,
    private userDataService: UsersDataService,
    _accessManagementAuthService: AccessManagementFrontendAuthService
  ) {}

  public async ngOnInit(): Promise<void> {
    this.isLoading = true;

    try {
      const usersResponse = await firstValueFrom(
        this.userDataService.getUsers()
      );
      this.users = usersResponse.data;

      this.activatedRoute.paramMap
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((paramMap: ParamMap): void => {
          const idParam = paramMap.get('id');
          if (idParam && idParam !== 'new') {
            this.selectedUserCollectionId = parseInt(idParam, 10);

            this.userCollectionService
              .getUserCollectionById(this.selectedUserCollectionId)
              .pipe(take(1))
              .subscribe((response) => {
                this.currentUserCollectionUsers =
                  response.data.userCollectionUsers;
                this.currentUserCollection = response.data;
                this.isLoading = false;
              });
          } else {
            if (this.currentUserCollection) {
              this.userCollectionEditComponent.userCollection = undefined;
              this.userCollectionEditComponent.userCollectionForm.reset();
            }
            this.selectedUserCollectionId = 'new';
            this.isLoading = false;
          }
        });
    } catch (_error) {
      // console.error('Error during initialization:', error);
      this.isLoading = false;
    }
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public async onSubmitEdit(event: Event): Promise<void> {
    const userCollectionData = event as unknown as {
      name: string;
      description: string;
      users: number[];
    };

    const payload = {
      name: userCollectionData.name,
      description: userCollectionData.description,
    };

    // Check for duplicate user collection name (case-insensitive)
    const existingCollectionsResponse = await firstValueFrom(
      this.userCollectionService.getUserCollections()
    );

    if (existingCollectionsResponse?.data && Array.isArray(existingCollectionsResponse.data)) {
      const duplicateCollection = existingCollectionsResponse.data.find(
        (collection) =>
          collection.name.toLowerCase() === userCollectionData.name.toLowerCase() &&
          collection.id !== this.selectedUserCollectionId
      );

      if (duplicateCollection) {
        this.snackbar.openFromComponent(ErrorSnackbarComponent, {
          data: `A user collection with the name "${userCollectionData.name}" already exists. Please choose a different name.`,
        });
        return;
      }
    }

    if (
      this.selectedUserCollectionId &&
      this.selectedUserCollectionId !== 'new'
    ) {
      await this.userCollectionService
        .updateUserCollection(
          payload,
          userCollectionData.users,
          this.selectedUserCollectionId
        )
        .then((val) => {
          val.pipe(take(1)).subscribe(async (response) => {
            if (response.success) {
              this.snackbar.open('User collection updated successfully.', 'OK', { duration: 6000 });
              // Mark form as pristine to prevent "unsaved changes" dialog on navigation
              this.userCollectionEditComponent.userCollectionForm.markAsPristine();
              await this.router.navigate(['/administration/access-management']);
            } else {
              this.snackbar.openFromComponent(ErrorSnackbarComponent, {
                data: 'An unexpected error occurred trying to update the user collection',
              });
            }
          });
        });
    } else {
      this.userCollectionService
        .addUserCollection(payload, userCollectionData.users)
        .pipe(take(1))
        .subscribe(async (response) => {
          if (response.success) {
            this.snackbar.open('User collection created successfully.', 'OK', { duration: 6000 });
            // Mark form as pristine to prevent "unsaved changes" dialog on navigation
            this.userCollectionEditComponent.userCollectionForm.markAsPristine();
            await this.router.navigate(['/administration/access-management']);
          } else {
            this.snackbar.openFromComponent(ErrorSnackbarComponent, {
              data: 'An unexpected error occurred trying to create the user collection',
            });
          }
        });
    }
  }

  public async onCancelEdit(): Promise<void> {
    await this.router.navigate(['/administration/access-management']);
  }

  public onDeleteUserCollection(): void {
    if (this.selectedUserCollectionId === 'new') {
      return;
    }

    // Check if user collection can be deleted before showing confirmation dialog
    this.userCollectionService.canDeleteUserCollection(this.selectedUserCollectionId)
      .pipe(take(1))
      .subscribe((canDeleteResponse) => {
        if (!canDeleteResponse.canDelete) {
          // Show error immediately without confirmation dialog
          this.snackbar.openFromComponent(ErrorSnackbarComponent, {
            data: canDeleteResponse.message || 'Error deleting user collection, it is assigned to a designation',
          });
          return;
        }

        // User collection can be deleted, show confirmation dialog
        const deleteDialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
          data: {
            title: 'Confirmation',
            message: 'Are you sure you want to delete this user collection?',
            type: 'warning',
            layout: 'warning-box',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            confirmButtonColor: 'warn',
            hard: true,
          },
          width: '540px',
          maxWidth: '100%',
          disableClose: true,
          hasBackdrop: true,
        });

        deleteDialogRef
          .afterClosed()
          .pipe(take(1))
          .subscribe(async (result: ConfirmationDialogResult | undefined): Promise<void> => {
            if (
              result?.confirmed &&
              this.selectedUserCollectionId &&
              this.selectedUserCollectionId !== 'new'
            ) {
              (
                await this.userCollectionService.deleteUserCollection(
                  this.selectedUserCollectionId
                )
              )
                .pipe(take(1))
                .subscribe(async (response) => {
                  if (response.success) {
                    this.snackbar.open(
                      'User collection deleted successfully.',
                      'OK',
                      { duration: 6000 }
                    );
                    await this.router.navigate([
                      '/administration/access-management',
                    ]);
                  } else {
                    this.snackbar.openFromComponent(ErrorSnackbarComponent, {
                      data: 'An error occurred while deleting the user collection.',
                    });
                  }
                });
            }
          });
      });
  }

  public onOpenCsvUpload(): void {
    const dialogRef = this.dialog.open(UserCollectionCsvUploadDialogComponent, {
      disableClose: true,
      hasBackdrop: true,
      data: { emailExtractionMode: true },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: { success: boolean; emails?: string[] }) => {
        if (result?.success && result.emails && result.emails.length > 0) {
          // Pass the emails to the user collection edit component
          this.userCollectionEditComponent.addUsersByEmails(result.emails);
        }
      });
  }

  public getTitleText(): string {
    if (this.selectedUserCollectionId !== 'new' && this.currentUserCollection) {
      return `Edit User Collection: ${this.currentUserCollection.name}`;
    }
    return 'Add New User Collection';
  }

  /**
   * Confirms navigation when user has unsaved changes
   * Shows a warning dialog to prevent accidental data loss
   * @returns Observable<boolean> - true if navigation allowed, false if blocked
   */
  confirmNavigationWithUnsavedChanges(): Observable<boolean> | boolean {
    // Check if form is dirty (has unsaved changes)
    // Form is marked dirty when name/description changes OR when user list is modified
    if (!this.userCollectionEditComponent?.userCollectionForm?.dirty) {
      return true;
    }

    // Show confirmation dialog to warn user about data loss
    const dialogData: CdwConfirmationDialogData = {
      title: 'Confirmation',
      message:
        'You have unsaved changes. Are you sure you would like to continue?',
      type: 'warning',
      size: 'medium',
      layout: 'warning-box',
      fontFamily: 'Roboto',
      confirmText: 'Leave Page',
      cancelText: 'Stay on Page',
      confirmButtonColor: 'warn',
      showCancelButton: true,
      hard: true,
    };

    const dialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true, // Prevent Escape key and backdrop click from closing
    });

    // Return observable that resolves based on user's choice
    return dialogRef.afterClosed().pipe(
      map((result: { confirmed: boolean; dontShowAgain?: boolean }) => {
        return result?.confirmed ?? false;
      })
    );
  }
}
